<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <div v-show="!isLoading">
    <h1 class="font-semibold text-xl text-grey-light-900">Billing & Fees</h1>
    <p class="text-grey-light-600 text-sm">Manage your LayUp subscription.</p>
    <SectionDivider class="my-4" />
    <div class="grid gap-4 lg:grid-cols-5 sm:grid-cols-1">
      <div class="col-span-2">
        <h5 class="font-semibold text-grey-light-900">Plan Details</h5>
        <p class="text-grey-light-600 text-sm">View and manage your current plan.</p>
      </div>
      <div class="col-span-2 md:col-span-3">
        <div v-if="!hideBilling" class="flex flex-col gap-6 rounded-lg p-6 border border-grey-light-200">
          <div class="flex flex-col lg:flex-row justify-between gap-2">
            <div class="flex flex-col gap-2">
              <div class="flex gap-4">
                <h1 class="font-semibold text-xl text-grey-light-900">{{ accountType }}</h1>
                <Badge variant="outline" class="rounded-lg">Monthly</Badge>
              </div>
              <p class="text-grey-light-600 text-sm">Our most popular plan for small teams</p>
            </div>
            <div class="flex items-center">
              <h3 class="font-semibold text-grey-light-900 text-3xl mr-2">R</h3>
              <h2 class="font-semibold text-grey-light-900 text-4xl">{{ (monthlyFee / 100).toFixed(2) }}</h2>
              <p class="text-grey-light-600 font-medium text-lg">/pm</p>
            </div>
          </div>
          <ul class="flex flex-col gap-3">
            <li class="flex justify-between">
              <span class="text-grey-light-700">Settlement type</span>
              <span class="text-grey-light-900">{{ settlementType }}</span>
            </li>
            <li class="flex justify-between">
              <span class="text-grey-light-700">Settlement frequency</span>
              <span class="text-grey-light-900">{{ settlementFrequency }}</span>
            </li>
            <li class="flex justify-between">
              <span class="text-grey-light-700">Settlement Day</span>
              <span class="text-grey-light-900">{{ settlementDay }}</span>
            </li>
            <li class="flex justify-between">
              <span class="text-grey-light-700">Number of users</span>
              <span class="text-grey-light-900">{{ noOfUsers }}</span>
            </li>
          </ul>
          <div>
            <p class="font-medium text-gray-700 mb-2">{{ settings.numberOfAddedUsers }} of {{ noOfUsers }} users</p>
            <div class="relative pb-4">
              <div class="absolute left-0 right-0 h-2 rounded-full bg-grey-light-200"></div>
              <div
                class="absolute left-0 h-2 rounded-full bg-brand-700"
                :style="{ width: `${(settings.numberOfAddedUsers / noOfUsers) * 100}%` }"
              ></div>
            </div>
          </div>
          <SectionDivider class="my-0" />
          <div class="flex justify-end">
            <Button variant="primaryOutline" class="w-auto">
              Upgrade Plan
              <ArrowUpRight class="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div v-else>
          <p class="text-grey-light-600 text-sm">No billing and fees assigned</p>
        </div>
      </div>
    </div>
    <SectionDivider class="my-4" />
    <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
      <div class="col-span-2">
        <h5 class="font-semibold text-grey-light-900">Billing Method</h5>
      </div>
      <div class="col-span-2 md:col-span-3">
        <div class="flex items-center justify-between mb-4">
          <div class="flex flex-col gap-2">
            <span class="font-medium">Card Details</span>
            <span class="text-grey-light-600 text-sm">Add or remove cards. A maximum of 3 cards can be added.</span>
          </div>
          <Button
            @click="showCardDetails = !showCardDetails"
            class="self-center ml-auto"
            variant="outline"
            size="sm"
            type="button"
          >
            <ChevronsUpDown class="h-4 w-4" />
            Show / Hide
          </Button>
        </div>
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 max-h-0 overflow-hidden"
          enter-to-class="opacity-100 max-h-96 overflow-visible"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="opacity-100 max-h-96 overflow-visible"
          leave-to-class="opacity-0 max-h-0 overflow-hidden"
        >
          <template v-if="showCardDetails">
            <LayupSpinner v-if="isLoadingCardSection" />
            <template v-else>
              <div>
                <template v-if="merchantCards">
                  <ul class="flex flex-col gap-2 mb-2" v-for="(card, index) in merchantCards" :key="index">
                    <li class="flex gap-4 border border-grey-light-200 rounded-lg p-4">
                      <div>
                        <img
                          src="@/assets/paymentMethods/Visa.svg"
                          class="w-12"
                          alt="Visa logo"
                          v-if="card.cardType === 'VISA'"
                        />
                        <img
                          src="@/assets/paymentMethods/MasterCard.svg"
                          class="w-12"
                          alt="Visa logo"
                          v-if="card.cardType === 'MASTER'"
                        />
                      </div>
                      <div class="flex flex-col gap-2">
                        <p class="text-grey-light-700 font-medium">
                          Visa ending in {{ card.cardDigits }}
                          <span
                            class="inline-flex items-center rounded-md bg-branded-600 px-2 py-1 text-xs font-medium text-branded-600 ring-1 ring-branded-600/20 ring-inset"
                            v-if="card.defaultCard"
                          >
                            Default
                          </span>
                        </p>
                        <p class="text-grey-light-600">Expiry {{ card.expiryMonth }}/{{ card.expiryYear }}</p>
                        <div class="flex gap-2">
                          <Button
                            v-if="!card.defaultCard && merchantCards.length > 1"
                            variant="destructive"
                            size="sm"
                            @click="handleOpenDelete(card)"
                          >
                            Remove
                          </Button>

                          <Button
                            variant="primaryOutline"
                            size="sm"
                            class="w-auto"
                            v-if="!card.defaultCard"
                            @click="onSetDefaultCard(card)"
                          >
                            Set as default
                          </Button>
                        </div>
                      </div>
                    </li>
                  </ul>
                </template>
                <p v-else class="mb-2 text-sm text-grey-light-600">No cards added</p>
                <Button
                  v-if="merchantCards.length < 3"
                  variant="outline"
                  class="w-auto mr-2"
                  @click.prevent="onAddCard"
                >
                  <Plus class="h-4 w-4" />
                  Add Card
                </Button>
              </div>
            </template>
          </template>
        </Transition>
        <div class="overflow-x-auto col-span-2 mt-4" v-if="debitOrderMandate">
          <div class="flex items-center justify-between mb-4">
            <div class="flex flex-col gap-2">
              <span class="font-medium">Debit Order Mandates</span>
              <span class="text-grey-light-600 text-sm">Setup a debit order for monthly payments.</span>
            </div>
            <Button
              @click="showDebitOrderMandate = !showDebitOrderMandate"
              class="self-center ml-auto"
              variant="outline"
              size="sm"
              type="button"
            >
              <ChevronsUpDown class="h-4 w-4" />
              Show / Hide
            </Button>
          </div>
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 max-h-0 overflow-hidden"
            enter-to-class="opacity-100 max-h-96 overflow-visible"
            leave-active-class="transition-all duration-300 ease-in"
            leave-from-class="opacity-100 max-h-96 overflow-visible"
            leave-to-class="opacity-0 max-h-0 overflow-hidden"
          >
            <template v-if="showDebitOrderMandate">
              <LayupSpinner v-if="isLoadingDebitOrder" />
              <div v-else>
                <template v-if="debitOrderMandate.accountNumber">
                  <table class="min-w-full divide-y divide-gray-200 rounded-lg shadow-md mb-2">
                    <thead class="bg-gray-100">
                      <tr>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Reference
                        </th>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Account Number
                        </th>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Account Type
                        </th>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Branch Code
                        </th>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Bank Name
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {{ debitOrderMandate.reference }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {{ debitOrderMandate.accountNumber }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {{ debitOrderMandate.accountType }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {{ debitOrderMandate.branchCode }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {{ banks.find((bank) => bank.branchCode === debitOrderMandate.branchCode)?.name }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
                <p v-else class="mb-2 text-sm text-grey-light-600">No debit order mandates created</p>
                <Button variant="outline" class="w-auto" @click.prevent="isCreateOrReplaceDebitOrder = true">
                  <Plus class="h-4 w-4" />
                  {{ debitOrderMandate.accountNumber ? 'Replace' : 'Add' }} Debit Order
                </Button>
              </div>
            </template>
          </Transition>
        </div>
      </div>
    </div>
    <SectionDivider class="my-4" />
    <template v-if="!hideBilling">
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Transaction Fees</h5>
          <p class="text-grey-light-600 text-sm">View what you are being charged per transaction type.</p>
        </div>
        <div class="col-span-2 md:col-span-3">
          <div class="flex flex-col gap-4">
            <div class="border rounded-lg overflow-hidden">
              <div class="bg-gray-50 p-4 flex items-center gap-2 font-semibold text-gray-600 border-b">
                <span class="font-semibold">Transaction Payment Method</span>
                <CircleHelp class="h-4 w-4" />
              </div>
              <table class="w-full text-sm text-gray-700">
                <tbody>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Method</td>
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Per Transaction</td>
                  </tr>
                  <tr v-for="(plan, index) in transactionsPaymentMethods" :key="index" class="border-b">
                    <td class="px-4 py-3 w-1/2">
                      {{ capitalize(plan.method) }}
                    </td>
                    <td class="px-4 py-3 w-1/2">
                      {{
                        [plan.percentage > 0 ? `${plan.percentage}%` : '', plan.flat > 0 ? currency(plan.flat) : '']
                          .filter(Boolean)
                          .join(' + ')
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="border rounded-lg overflow-hidden">
              <div class="bg-gray-50 p-4 flex items-center gap-2 font-semibold text-gray-600 border-b">
                <span class="font-semibold">Transaction Payment Type</span>
                <CircleHelp class="h-4 w-4" />
              </div>
              <table class="w-full text-sm text-gray-700">
                <tbody>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Type</td>
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Per Transaction</td>
                  </tr>
                  <tr v-for="(plan, index) in transactionsPaymentType" :key="index" class="border-b">
                    <td class="px-4 py-3 w-1/2">
                      {{ capitalize(plan.method) }}
                    </td>
                    <td class="px-4 py-3 w-1/2">
                      {{ currency(plan.flat) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Payment Plan Fees</h5>
          <p class="text-grey-light-600 text-sm">View what you are being charged per transaction type.</p>
        </div>
        <div class="col-span-2 md:col-span-3">
          <div class="flex flex-col gap-4">
            <div class="border rounded-lg overflow-hidden">
              <div class="bg-gray-50 p-4 flex items-center gap-2 font-semibold text-gray-600 border-b">
                <span class="font-semibold">Payment Plan Type</span>
                <CircleHelp class="h-4 w-4" />
              </div>
              <table class="w-full text-sm text-gray-700">
                <tbody>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Type</td>
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Per Payment Plan</td>
                  </tr>
                  <tr v-for="(plan, index) in paymentPlanType" :key="index" class="border-b">
                    <td class="px-4 py-3 w-1/2">
                      {{ capitalize(plan.method) }}
                    </td>
                    <td class="px-4 py-3 w-1/2">
                      {{
                        [plan.percentage > 0 ? `${plan.percentage}%` : '', plan.flat > 0 ? currency(plan.flat) : '']
                          .filter(Boolean)
                          .join(' + ')
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="border rounded-lg overflow-hidden">
              <div class="bg-gray-50 p-4 flex items-center gap-2 font-semibold text-gray-600 border-b">
                <span class="font-semibold">Payment Plan State</span>
                <CircleHelp class="h-4 w-4" />
              </div>
              <table class="w-full text-sm text-gray-700">
                <tbody>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2 text-left font-bold">State</td>
                    <td class="px-4 py-3 w-1/2 text-left font-bold">Per Payment Plan</td>
                  </tr>
                  <tr v-for="(plan, index) in paymentPlanState" :key="index" class="border-b">
                    <td class="px-4 py-3 w-1/2">
                      {{ capitalize(plan.method) }}
                    </td>
                    <td class="px-4 py-3 w-1/2">
                      {{
                        [plan.percentage > 0 ? `${plan.percentage}%` : '', plan.flat > 0 ? currency(plan.flat) : '']
                          .filter(Boolean)
                          .join(' + ')
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Billing & settlements</h5>
          <p class="text-grey-light-600 text-sm">Allow users to pay instalments on a weekly basis.</p>
        </div>
        <div class="col-span-2 md:col-span-3">
          <div class="flex flex-col gap-4">
            <div class="border rounded-lg overflow-hidden">
              <div class="bg-gray-50 p-4 flex items-center gap-2 font-semibold text-gray-600 border-b">
                <span class="font-semibold">Billing & Settlements</span>
                <CircleHelp class="h-4 w-4" />
              </div>
              <table class="w-full text-sm text-gray-700">
                <tbody>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2">Transaction fee</td>
                    <td class="px-4 py-3 w-1/2 border-l">{{ transactionFee }}%</td>
                  </tr>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2">Settlement type</td>
                    <td class="px-4 py-3 w-1/2 border-l">{{ settlementType }}</td>
                  </tr>
                  <tr class="border-b">
                    <td class="px-4 py-3 w-1/2">Settlement frequency</td>
                    <td class="px-4 py-3 w-1/2 border-l">{{ settlementFrequency }}</td>
                  </tr>
                  <tr>
                    <td class="px-4 py-3 w-1/2">Settlement day</td>
                    <td class="px-4 py-3 w-1/2 border-l">{{ settlementDay }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Button type="button" variant="outline" class="mt-4" @click="isRequestBillingChangeRequest = true">
              Request Changes
            </Button>
          </div>
        </div>
      </div>
    </template>

    <RequestBillingChange
      :open="isRequestBillingChangeRequest"
      :isLoading="isLoading"
      :changeRequestErrorMessage="changeRequestErrorMessage"
      @update:open="isRequestBillingChangeRequest = $event"
      @submit="onSubmit"
    />

    <CreateDebitOrder
      v-if="isCreateOrReplaceDebitOrder"
      :isCreateOrReplaceDebitOrder="isCreateOrReplaceDebitOrder"
      :debit-order="debitOrderRequest"
      @disable:create="isCreateOrReplaceDebitOrder = false"
      :banks="banks"
      @get:debit-order="getActiveDebitOrderMandate"
    />

    <DeleteCard
      :open="openDeleteCardModal"
      :cardNumber="selectedCard.cardDigits"
      :cardId="selectedCard._id"
      @update:open="openDeleteCardModal = $event"
      @update:get-cards="getMerchantCards"
    />

    <AddCard
      v-if="isAddOrReplaceCard"
      :shouldOpenModal="isAddOrReplaceCard"
      @disable:modal="isAddOrReplaceCard = false"
      @card:added="getMerchantCards"
    />

    <AddCardConfirmation v-model:open="showAddCardConfirmation" @update:on-accept="onAddCardConfirmed" />

    <PlanUpgrade
      :open="openUpdatePlanModal"
      :merchantId="props.merchantId"
      :currentPlan="props.settings.accountType"
      :plans="['Starter', 'Business', 'Pro', 'Enterprise']"
      @update:open="openUpdatePlanModal = $event"
      :merchantName="props.settings.name"
    />
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import { fetchBanks } from '@/services/banks';
import { getCards, updateCard } from '@/services/cards';
import { getDebitOrderMandate } from '@/services/debitOrder';
import { sendEmail } from '@/services/emails';
import { currency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ArrowUpRight, ChevronsUpDown, CircleHelp, Plus } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import type {
  ModelBankResponse,
  ModelCardResponse,
  ModelDebitOrderMandateResponse,
  ModelUserResponse,
} from '@layup/api-sdk';
import AddCard from '../billing-fees/AddCard.vue';
import AddCardConfirmation from '../billing-fees/AddCardConfirmation.vue';
import CreateDebitOrder from '../billing-fees/CreateDebitOrder.vue';
import DeleteCard from '../billing-fees/DeleteCard.vue';
import PlanUpgrade from '../billing-fees/PlanUpgrade.vue';
import RequestBillingChange from '../billing-fees/RequestBillingChange.vue';
import {
  capitalize,
  type BillingChangeRequest,
  type BillingSettings,
  type DebitOrderMandateCreateRequest,
} from '../utils';

const props = defineProps<{
  settings: BillingSettings;
  merchantId: string;
  user: ModelUserResponse;
}>();

const accountType = ref('');
const settlementFrequency = ref('');
const transactionFee = ref(0);
const settlementType = ref('');
const settlementDay = ref('');
const monthlyFee = ref(0);
const noOfUsers = ref(0);
const transactionsPaymentMethods = ref([] as any);
const transactionsPaymentType = ref([] as any);
const paymentPlanType = ref([] as any);
const paymentPlanState = ref([] as any);
const isRequestBillingChangeRequest = ref(false);
const requestDescription = ref('');
const errorMessage = ref('');
const changeRequestErrorMessage = ref('');
const isLoading = ref(false);
const isLoadingCardSection = ref(false);
const toast = useToast();
const merchantCards = ref([] as ModelCardResponse[]);
const isAddOrReplaceCard = ref(false);
const showAddCardConfirmation = ref(false);
const isCreateOrReplaceDebitOrder = ref(false);
const debitOrderRequest = ref({} as DebitOrderMandateCreateRequest);
const debitOrderMandate = ref({} as ModelDebitOrderMandateResponse);
const banks = ref([] as ModelBankResponse[]);
const openUpdatePlanModal = ref(false);
const openDeleteCardModal = ref(false);
const selectedCard = ref({} as ModelCardResponse);
const showCardDetails = ref(true);
const showDebitOrderMandate = ref(true);
const isLoadingDebitOrder = ref(false);
const hideBilling = ref(false);

const syncFromSettings = () => {
  settlementFrequency.value = props.settings?.settlementFrequency;
  transactionFee.value = props.settings?.percFee;
  noOfUsers.value = props.settings.accountSettings?.numberOfUsers || 0!;

  const settlementTypeMap = {
    ALL: 'ALL',
    COMPLETEDCANCELLED: 'COMPLETED & CANCELLED',
    DEPOSITCOMPLETEDCANCELLED: 'DEPOSIT, COMPLETED & CANCELLED',
    DEPOSITCOMPLETED: 'DEPOSIT & COMPLETED',
    COMPLETED: 'COMPLETED',
  };

  settlementType.value =
    settlementTypeMap[props.settings.settlementType as keyof typeof settlementTypeMap] || settlementType.value;

  const monthlyDayMap = {
    1: '1st',
    2: '2nd',
    3: '3rd',
    4: '4th',
    5: '5th',
    6: '6th',
    7: '7th',
    8: '8th',
    9: '9th',
    10: '10th',
    11: '11th',
    12: '12th',
    13: '13th',
    14: '14th',
    15: '15th',
    16: '16th',
    17: '17th',
    18: '18th',
    19: '19th',
    20: '20th',
    21: '21st',
    22: '22nd',
    23: '23rd',
    24: '24th',
    25: '25th',
    26: '26th',
    27: '27th',
    28: '28th',
    29: '29th',
    30: '30th',
    31: '31st',
  };

  const weeklyDayMap = {
    1: 'SUNDAY',
    2: 'MONDAY',
    3: 'TUESDAY',
    4: 'WEDNESDAY',
    5: 'THURSDAY',
    6: 'FRIDAY',
    7: 'SATURDAY',
  };

  switch (props.settings.settlementFrequency) {
    case 'DAILY':
      settlementDay.value = 'DAILY';
      break;
    case 'MONTHLY': {
      settlementDay.value = monthlyDayMap[props.settings.settlementDay as keyof typeof monthlyDayMap];
      break;
    }
    case 'WEEKLY':
      settlementDay.value = weeklyDayMap[props.settings.settlementDay as keyof typeof weeklyDayMap];
      break;
  }

  const products = props.settings?.priceList?.products;

  accountType.value = props.settings.accountType;
  const accountTypeProduct = products?.find((product: any) => product.feeType === 'accountType');
  if (accountTypeProduct) {
    monthlyFee.value = accountTypeProduct.flat;
  }
  hideBilling.value = !accountTypeProduct;

  // PriceList
  if (props.settings.priceList && props.settings.priceList?.products) {
    transactionsPaymentMethods.value = [];
    transactionsPaymentType.value = [];
    paymentPlanType.value = [];
    paymentPlanState.value = [];
    props.settings?.priceList?.products.forEach((product: any) => {
      if (product.feeType === 'paymentMethod') {
        const paymentMethodConditions = {
          paymentMethodPeachCard:
            props.settings.paymentPlans?.paymentMethods?.creditOrDebitCard || props.settings.features?.enableCreditCard,
          paymentMethodDebicheck:
            props.settings.paymentPlans?.paymentMethods?.debiCheck || props.settings.features?.enableDebiCheck,
          paymentMethodPayat:
            props.settings.paymentPlans?.paymentMethods?.payAt || props.settings.features?.enablePayAt,
          paymentMethodPeachEft:
            props.settings.paymentPlans?.paymentMethods?.eft || props.settings.features?.enableInstantEFT,
        };

        if (
          typeof product.name === 'string' &&
          product.name in paymentMethodConditions &&
          paymentMethodConditions[product.name as keyof typeof paymentMethodConditions]
        ) {
          transactionsPaymentMethods.value.push({ ...product, value: false });
        }
      }

      const typeArrayMap = {
        paymentType: transactionsPaymentType.value,
        paymentplanType: paymentPlanType.value,
        orderState: paymentPlanState.value,
      };

      if (product.feeType in typeArrayMap) {
        typeArrayMap[product.feeType as keyof typeof typeArrayMap].push(product);
      }
    });
  }
};

const init = async () => {
  isLoading.value = true;

  syncFromSettings();

  await getMerchantCards();

  debitOrderRequest.value = {
    merchantId: props.merchantId,
    reference: 'LAYUP-FEES',
    holderName: props.user.name!,
    holderSurname: props.user.name!,
    contact: props.user.cellNumber!,
    accountType: props.settings.bankDetails?.accountType! || '',
    accountNumber: props.settings.bankDetails?.accountNumber! || '',
    branchCode: props.settings.bankDetails?.branchCode! || '',
    identificationType: 'COMPANY_REGISTRATION_NUMBER',
    identificationNumber: props.settings.registeredEntity,
    bankName: props.settings.bankDetails?.bankName! || '',
  };

  await getActiveDebitOrderMandate();
  const response = await fetchBanks();
  banks.value = response.banks!;
  isLoading.value = false;
};

const getMerchantCards = async () => {
  try {
    isLoadingCardSection.value = true;
    const cards = await getCards();

    if (!cards) {
      merchantCards.value = [];
      return;
    }

    // Sorting cards making sure the default card is always at the top
    merchantCards.value = cards
      .sort((a, b) => (b.defaultCard! ? 1 : 0) - (a.defaultCard! ? 1 : 0))
      .filter((card) => card.cardDigits);
  } catch (error: any) {
    errorMessage.value = 'Something went wrong. Please try again later.';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('billing-and-fees-page', {
        status: 'network_error',
        channel: 'merchant',
        method: 'getMerchantCards',
      });
    } else {
      createException(error);
      addMetric('billing-and-fees-page', {
        status: 'error',
        channel: 'merchant',
        method: 'getMerchantCards',
      });
    }
    if (error?.response?.data) {
      errorMessage.value = error.response.data;
    }
  } finally {
    isLoadingCardSection.value = false;
  }
};

const getActiveDebitOrderMandate = async () => {
  try {
    isLoadingDebitOrder.value = true;
    debitOrderMandate.value = await getDebitOrderMandate(props.merchantId);
  } catch (error: any) {
    errorMessage.value = 'Something went wrong. Please try again later.';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('billing-and-fees-page', {
        status: 'network_error',
        channel: 'merchant',
        method: 'getActiveDebitOrderMandate',
      });
    } else {
      createException(error);
      addMetric('billing-and-fees-page', {
        status: 'error',
        channel: 'merchant',
        method: 'getActiveDebitOrderMandate',
      });
    }
    if (error.response.data) {
      errorMessage.value = error.response.data;
    }
  } finally {
    isLoadingDebitOrder.value = false;
  }
};

const handleOpenDelete = (card: ModelCardResponse) => {
  selectedCard.value = card;
  openDeleteCardModal.value = true;
};

const onAddCardConfirmed = () => {
  isAddOrReplaceCard.value = true;
  showAddCardConfirmation.value = false;
};

const onAddCard = async () => {
  try {
    showAddCardConfirmation.value = true;
    // isAddOrReplaceCard.value = true;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('billing-and-fees-page', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateCard:onSetDefaultCard',
      });
    } else {
      createException(error);
      addMetric('billing-and-fees-page', {
        status: 'error',
        channel: 'merchant',
        method: 'updateCard:onSetDefaultCard',
      });
    }
  }
};

const onSetDefaultCard = async (card: ModelCardResponse) => {
  try {
    isLoadingCardSection.value = true;
    await updateCard({ ...card, defaultCard: true });
    await getMerchantCards();
    toast.success('Card set as default successfully!', { position: 'top' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('billing-and-fees-page', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateCard:onSetDefaultCard',
      });
    } else {
      createException(error);
      addMetric('billing-and-fees-page', {
        status: 'error',
        channel: 'merchant',
        method: 'updateCard:onSetDefaultCard',
      });
    }
  } finally {
    isLoadingCardSection.value = false;
  }
};

const onSubmit = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  try {
    const data: BillingChangeRequest = {
      merchantName: props.settings.name,
      userName: props.user.name!,
      userEmail: props.user.email!,
      merchantId: props.merchantId,
      merchantContactNumber: props.settings.supportMobile,
      message: requestDescription.value,
      type: 'billing_change_request',
    };

    await sendEmail(data);
    toast.success('Change request sent to successfully!', { position: 'top' });
    requestDescription.value = '';
    isRequestBillingChangeRequest.value = false;
  } catch (error: any) {
    errorMessage.value = 'Something went wrong. Please try again later.';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('billing-and-fees-page', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateMerchantUser:onAddMerchant',
      });
    } else {
      createException(error);
      addMetric('billing-and-fees-page', {
        status: 'error',
        channel: 'merchant',
        method: 'updateMerchantUser:onAddMerchant',
      });
    }
    if (error.response.data) {
      errorMessage.value = error.response.data;
    }
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.settings,
  () => {
    syncFromSettings();
  },
  { deep: true },
);

init();
</script>
